import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  ChevronDownIcon,
  CheckIcon,
  CloudUploadIcon,
  CreditCardIcon,
  ImageIcon,
  LayoutTemplateIcon,
  LayoutGridIcon,
  MailIcon,
  PlusIcon,
  RefreshCcwIcon,
  SearchIcon,
  Settings2Icon,
  TablePropertiesIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Avatar, AvatarBadge, AvatarFallback, AvatarGroup, AvatarGroupCount } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const sampleRows = [
  {
    id: '1',
    initials: 'DA',
    name: 'Development Admin',
    email: 'dev@inersio.com',
    role: 'Admin',
    status: 'Actif',
    verification: 'Vérifié',
    createdAt: '13/03/2026',
  },
  {
    id: '2',
    initials: 'CL',
    name: 'Claire Laurent',
    email: 'claire@inersio.com',
    role: 'User',
    status: 'En attente',
    verification: 'À vérifier',
    createdAt: '11/03/2026',
  },
  {
    id: '3',
    initials: 'MB',
    name: 'Marc Bernard',
    email: 'marc@inersio.com',
    role: 'Admin',
    status: 'Désactivé',
    verification: 'Vérifié',
    createdAt: '07/03/2026',
  },
];

type DemoSortKey = 'createdAt' | 'email' | 'emailVerified' | 'name' | 'role' | 'status';
type DemoSortDirection = 'asc' | 'desc';

function ToolbarButton({
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      variant="outline"
      className={cn(
        'h-12 border-border bg-background px-5 text-[15px] font-medium text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80',
        className
      )}
      {...props}
    />
  );
}

function SortHeaderButton({
  active,
  children,
  direction,
  onClick,
}: React.PropsWithChildren<{
  active: boolean;
  direction: DemoSortDirection;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 font-medium text-foreground transition-colors hover:text-muted-foreground"
    >
      <span>{children}</span>
      {active ? (
        direction === 'asc' ? <ArrowUpIcon className="size-3.5 text-muted-foreground" /> : <ArrowDownIcon className="size-3.5 text-muted-foreground" />
      ) : (
        <ArrowDownIcon className="size-3.5 text-muted-foreground/50" />
      )}
    </button>
  );
}

function TruncatedValue({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('block min-w-0 max-w-full truncate', className)}>{children}</span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        <span>{children}</span>
      </TooltipContent>
    </Tooltip>
  );
}

export function DesignSystemPage() {
  const [formTheme, setFormTheme] = useState<'default' | 'compact'>('default');
  const [tableSortKey, setTableSortKey] = useState<DemoSortKey>('createdAt');
  const [tableSortDirection, setTableSortDirection] = useState<DemoSortDirection>('desc');
  const [isTableDemoLoading, setIsTableDemoLoading] = useState(false);
  const [showTableDemoSkeleton, setShowTableDemoSkeleton] = useState(false);

  useEffect(() => {
    if (!isTableDemoLoading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- keeps the demo skeleton visible only while the controlled demo loading state is active.
      setShowTableDemoSkeleton(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowTableDemoSkeleton(true);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isTableDemoLoading]);

  function simulateTableLoading(durationMs: number) {
    setIsTableDemoLoading(true);
    window.setTimeout(() => {
      setIsTableDemoLoading(false);
    }, durationMs);
  }

  function toggleTableSort(nextKey: DemoSortKey) {
    if (tableSortKey === nextKey) {
      setTableSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setTableSortKey(nextKey);
    setTableSortDirection(nextKey === 'createdAt' ? 'desc' : 'asc');
  }

  return (
    <section className="w-full space-y-6">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Composants UI</h1>
          <span className="inline-flex min-w-8 items-center justify-center rounded-full border border-border bg-card px-2.5 py-1 text-sm font-medium text-muted-foreground">
            Design system
          </span>
        </div>
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
          Référence visuelle des composants disponibles dans l’application, regroupés par thématique.
        </p>
      </div>

      <Tabs defaultValue="buttons" className="space-y-6">
        <TabsList className="h-auto flex-wrap rounded-[var(--radius-surface-3xl)] bg-muted/50 p-1">
          <TabsTrigger value="buttons">
            <CreditCardIcon />
            Boutons
          </TabsTrigger>
          <TabsTrigger value="forms">
            <Settings2Icon />
            Formulaire
          </TabsTrigger>
          <TabsTrigger value="layout">
            <LayoutTemplateIcon />
            Layout
          </TabsTrigger>
          <TabsTrigger value="tables">
            <TablePropertiesIcon />
            Table
          </TabsTrigger>
        </TabsList>

        <TabsContent value="buttons" className="mt-0">
          <div className="rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card p-6">
            <div className="space-y-1">
              <h2 className="text-2xl font-semibold text-foreground">Boutons</h2>
              <p className="text-sm text-muted-foreground">États et variantes principales du design system.</p>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5">
                <p className="text-sm font-medium text-foreground">Variantes</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button>Principal</Button>
                  <Button variant="outline">Secondaire</Button>
                  <Button variant="secondary">Secondaire fort</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button variant="destructive">Danger</Button>
                </div>
              </div>

              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5">
                <p className="text-sm font-medium text-foreground">Tailles et combinaisons</p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button size="sm">Small</Button>
                  <Button size="default">Default</Button>
                  <Button size="lg">Large</Button>
                  <Button size="icon" aria-label="Ouvrir une grille">
                    <LayoutGridIcon />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        Action menu
                        <ChevronDownIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>Voir le détail</DropdownMenuItem>
                      <DropdownMenuItem>Dupliquer</DropdownMenuItem>
                      <DropdownMenuItem variant="destructive">Supprimer</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5 lg:col-span-2">
                <p className="text-sm font-medium text-foreground">Actions de toolbar</p>
                <div className="mt-4 flex flex-col gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <ToolbarButton>
                          Options
                          <ChevronDownIcon className="size-4" />
                        </ToolbarButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-64">
                        <DropdownMenuLabel>Options de page</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>
                          <RefreshCcwIcon />
                          Actualiser la liste
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Settings2Icon />
                          Réinitialiser recherche et filtres
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <Button
                      variant="outline"
                      className="h-12 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground"
                    >
                      <PlusIcon className="size-4" />
                      Ajouter
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <ToolbarButton className="gap-2 px-4">
                      Filtres
                      <Settings2Icon className="size-4" />
                    </ToolbarButton>
                    <ToolbarButton className="justify-between sm:min-w-56">
                      Ordre personnalisé
                      <ChevronDownIcon className="size-4" />
                    </ToolbarButton>
                    <ToolbarButton className="gap-2 px-4">
                      <Settings2Icon className="size-4" />
                      Colonnes
                    </ToolbarButton>
                  </div>
                </div>
              </div>

              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5 lg:col-span-2">
                <p className="text-sm font-medium text-foreground">Badges</p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="inline-flex whitespace-nowrap rounded-full bg-muted px-2.5 py-1 text-sm font-medium text-foreground">
                    Admin
                  </span>
                  <span className="inline-flex whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                    Actif
                  </span>
                  <span className="inline-flex whitespace-nowrap rounded-full bg-orange-100 px-2.5 py-1 text-sm font-medium text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                    En attente
                  </span>
                  <span className="inline-flex whitespace-nowrap rounded-full bg-red-100 px-2.5 py-1 text-sm font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300">
                    Désactivé
                  </span>
                  <span className="inline-flex whitespace-nowrap rounded-full bg-sky-100 px-2.5 py-1 text-sm font-medium text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
                    Information
                  </span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="forms" className="mt-0">
          <div className="rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card p-6">
            <div className="space-y-1">
              <h2 className="text-2xl font-semibold text-foreground">Formulaire</h2>
              <p className="text-sm text-muted-foreground">Champs, choix et patterns de saisie disponibles.</p>
            </div>

            <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5">
                <FieldGroup>
                  <div className="grid gap-5 lg:grid-cols-2">
                    <Field>
                      <FieldLabel htmlFor="ui-showcase-name">Nom complet</FieldLabel>
                      <Input id="ui-showcase-name" defaultValue="Jean Dupont" />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="ui-showcase-email">Email</FieldLabel>
                      <Input id="ui-showcase-email" type="email" defaultValue="jean@example.com" />
                    </Field>
                  </div>

                  <Field>
                    <FieldLabel htmlFor="ui-showcase-search">Recherche</FieldLabel>
                    <div className="relative">
                      <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="ui-showcase-search"
                        className="pl-11"
                        placeholder="Rechercher un composant"
                      />
                    </div>
                    <FieldDescription>Pattern de champ avec icône de préfixe.</FieldDescription>
                  </Field>

                  <Field>
                    <FieldLabel>Choix</FieldLabel>
                    <RadioGroup value={formTheme} onValueChange={(value) => setFormTheme(value as 'default' | 'compact')} className="grid gap-3 md:grid-cols-2">
                      <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-surface-3xl)] border border-border bg-background p-4">
                        <RadioGroupItem value="default" id="ui-showcase-theme-default" className="mt-0.5" />
                        <div className="space-y-1">
                          <p className="font-medium text-foreground">Confort</p>
                          <p className="text-sm leading-6 text-muted-foreground">Formulaire aéré pour les pages principales.</p>
                        </div>
                      </label>
                      <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-surface-3xl)] border border-border bg-background p-4">
                        <RadioGroupItem value="compact" id="ui-showcase-theme-compact" className="mt-0.5" />
                        <div className="space-y-1">
                          <p className="font-medium text-foreground">Compact</p>
                          <p className="text-sm leading-6 text-muted-foreground">Variante plus dense pour les panneaux secondaires.</p>
                        </div>
                      </label>
                    </RadioGroup>
                  </Field>
                </FieldGroup>
              </div>

              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5">
                <p className="text-sm font-medium text-foreground">Bonnes pratiques</p>
                <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
                  <li>Un seul CTA principal par bloc.</li>
                  <li>Les focus states reposent sur `primary` pour les inputs.</li>
                  <li>Les options exclusives passent par `RadioGroup` plutôt que par des cards custom sans radio.</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 grid gap-6 xl:grid-cols-2">
              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5">
                <p className="text-sm font-medium text-foreground">Avatar</p>
                <div className="mt-4 flex flex-wrap items-center gap-5">
                  <Avatar className="size-16 bg-card ring-1 ring-border ring-offset-2 ring-offset-background">
                    <AvatarFallback className="bg-muted text-base font-semibold text-foreground">DA</AvatarFallback>
                  </Avatar>

                  <Avatar className="size-16 bg-card ring-1 ring-border ring-offset-2 ring-offset-background">
                    <AvatarFallback className="bg-muted text-base font-semibold text-foreground">CL</AvatarFallback>
                    <AvatarBadge>
                      <CheckIcon />
                    </AvatarBadge>
                  </Avatar>

                  <AvatarGroup>
                    <Avatar size="lg">
                      <AvatarFallback className="bg-muted font-semibold text-foreground">DA</AvatarFallback>
                    </Avatar>
                    <Avatar size="lg">
                      <AvatarFallback className="bg-muted font-semibold text-foreground">CL</AvatarFallback>
                    </Avatar>
                    <Avatar size="lg">
                      <AvatarFallback className="bg-muted font-semibold text-foreground">MB</AvatarFallback>
                    </Avatar>
                    <AvatarGroupCount>+2</AvatarGroupCount>
                  </AvatarGroup>
                </div>
              </div>

              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-5">
                <p className="text-sm font-medium text-foreground">Drop zone</p>
                <div className="mt-4 rounded-[var(--radius-surface-3xl)] border border-dashed border-border bg-muted/20 p-6 text-center">
                  <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-background text-muted-foreground ring-1 ring-border">
                    <CloudUploadIcon className="size-6" />
                  </div>
                  <div className="mt-4 space-y-1">
                    <p className="text-[15px] font-medium text-foreground">Dépose un fichier ici</p>
                    <p className="text-sm text-muted-foreground">ou clique pour sélectionner une image</p>
                  </div>
                  <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <ImageIcon className="size-3.5" />
                    JPEG, PNG ou WebP, 15 Mo max.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="layout" className="mt-0">
          <div className="rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card p-6">
            <div className="space-y-1">
              <h2 className="text-2xl font-semibold text-foreground">Layout</h2>
              <p className="text-sm text-muted-foreground">Containers, surfaces et structures de page réutilisables.</p>
            </div>

            <div className="mt-6 space-y-6">
              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-6">
                <p className="text-sm font-medium text-foreground">Container de page</p>
                <div className="mt-4 rounded-[var(--radius-surface-3xl)] border border-dashed border-border bg-card p-6">
                  <div className="mx-auto w-full max-w-6xl space-y-4">
                    <div className="space-y-1">
                      <div className="h-7 w-56 rounded-full bg-muted" />
                      <div className="h-4 w-80 rounded-full bg-muted" />
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                      <div className="h-28 rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background" />
                      <div className="h-28 rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background" />
                      <div className="h-28 rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)]">
                <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-6">
                  <p className="text-sm font-medium text-foreground">Card principale</p>
                  <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <div className="h-6 w-48 rounded-full bg-muted" />
                      <div className="h-4 w-full max-w-md rounded-full bg-muted" />
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="h-24 rounded-[var(--radius-surface-2xl)] bg-muted/50" />
                      <div className="h-24 rounded-[var(--radius-surface-2xl)] bg-muted/50" />
                    </div>
                  </div>
                </div>

                <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-6">
                  <p className="text-sm font-medium text-foreground">Panneau secondaire</p>
                  <div className="mt-4 space-y-3">
                    <div className="h-5 w-28 rounded-full bg-muted" />
                    <div className="h-4 w-full rounded-full bg-muted" />
                    <div className="h-4 w-4/5 rounded-full bg-muted" />
                    <div className="h-10 rounded-[var(--radius-surface-2xl)] bg-muted/50" />
                  </div>
                </div>
              </div>

              <div className="rounded-[var(--radius-surface-3xl)] border border-border/70 bg-background p-6">
                <p className="text-sm font-medium text-foreground">Carte de traitement audio</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Pattern de suivi multi-fichiers : résumé live, lignes par fichier, actions selon l’état.
                </p>
                <div className="mt-4 w-full max-w-xl rounded-[var(--radius-surface-2xl)] border border-border/70 bg-card/90 px-3.5 py-3">
                  <p className="text-sm font-semibold text-foreground">Traitement de 3 fichiers audio</p>
                  <p className="mt-1 text-xs text-muted-foreground">1 sur 3 terminé</p>
                  <div className="mt-3 space-y-2">
                    <div className="rounded-[var(--radius-surface-2xl)] border border-border/60 px-3 py-2 text-sm">
                      réunion.mp3 · Terminé
                    </div>
                    <div className="rounded-[var(--radius-surface-2xl)] border border-border/60 px-3 py-2 text-sm text-muted-foreground">
                      atelier.wav · Transcription
                    </div>
                    <div className="rounded-[var(--radius-surface-2xl)] border border-destructive/25 bg-destructive/5 px-3 py-2 text-sm">
                      appel.m4a · Échec
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tables" className="mt-0">
          <div className="rounded-[var(--radius-surface-4xl)] border border-border/70 bg-card">
            <div className="border-b border-border/70 bg-[#f4f4f538] p-4 sm:p-5 dark:bg-muted/40">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="flex w-full max-w-[42rem] items-center gap-2">
                  <div className="relative min-w-0 max-w-[34rem] flex-1">
                    <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="h-12 border-border bg-background pl-11 text-[15px] shadow-none placeholder:text-muted-foreground dark:bg-muted"
                      placeholder="Rechercher les membres de l’équipe"
                    />
                  </div>
                  <ToolbarButton className="shrink-0 gap-2 px-4">
                    Filtres
                    <Settings2Icon className="size-4" />
                  </ToolbarButton>
                </div>

                <div className="flex items-center justify-end gap-2 xl:ml-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => simulateTableLoading(80)}
                  >
                    Chargement court
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-sm text-muted-foreground hover:text-foreground"
                    onClick={() => simulateTableLoading(900)}
                  >
                    Chargement long
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <ToolbarButton className="justify-between xl:min-w-56">
                        Ordre personnalisé
                        <ChevronDownIcon className="size-4" />
                      </ToolbarButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>Plus récents d’abord</DropdownMenuItem>
                      <DropdownMenuItem>Plus anciens d’abord</DropdownMenuItem>
                      <DropdownMenuItem>Comptes actifs d’abord</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <ToolbarButton className="gap-2 px-4">
                    <Settings2Icon className="size-4" />
                    Colonnes
                  </ToolbarButton>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="px-6 py-5">
                      <SortHeaderButton active={tableSortKey === 'name'} direction={tableSortDirection} onClick={() => toggleTableSort('name')}>
                        Nom
                      </SortHeaderButton>
                    </th>
                    <th className="px-6 py-5">
                      <SortHeaderButton active={tableSortKey === 'email'} direction={tableSortDirection} onClick={() => toggleTableSort('email')}>
                        Email
                      </SortHeaderButton>
                    </th>
                    <th className="px-6 py-5">
                      <SortHeaderButton active={tableSortKey === 'role'} direction={tableSortDirection} onClick={() => toggleTableSort('role')}>
                        Rôle
                      </SortHeaderButton>
                    </th>
                    <th className="px-6 py-5">
                      <SortHeaderButton active={tableSortKey === 'status'} direction={tableSortDirection} onClick={() => toggleTableSort('status')}>
                        Statut
                      </SortHeaderButton>
                    </th>
                    <th className="px-6 py-5">
                      <SortHeaderButton active={tableSortKey === 'emailVerified'} direction={tableSortDirection} onClick={() => toggleTableSort('emailVerified')}>
                        Vérification
                      </SortHeaderButton>
                    </th>
                    <th className="px-6 py-5">
                      <SortHeaderButton active={tableSortKey === 'createdAt'} direction={tableSortDirection} onClick={() => toggleTableSort('createdAt')}>
                        Créé le
                      </SortHeaderButton>
                    </th>
                    <th className="px-6 py-5 text-right font-medium text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isTableDemoLoading && showTableDemoSkeleton
                    ? Array.from({ length: 3 }).map((_, index) => (
                        <tr key={`design-system-skeleton-${index}`}>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <div className="flex items-center gap-4">
                              <Skeleton className="size-12 rounded-full" />
                              <div className="space-y-2">
                                <Skeleton className="h-5 w-48 rounded-full" />
                                <Skeleton className="h-4 w-24 rounded-full" />
                              </div>
                            </div>
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <Skeleton className="h-5 w-56 rounded-full" />
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <Skeleton className="h-8 w-24 rounded-full" />
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <Skeleton className="h-8 w-24 rounded-full" />
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <Skeleton className="h-8 w-28 rounded-full" />
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <Skeleton className="h-5 w-36 rounded-full" />
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <Skeleton className="ml-auto h-10 w-28 rounded-full" />
                          </td>
                        </tr>
                      ))
                    : sampleRows.map((row) => (
                        <tr key={row.id} className="align-middle">
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <div className="flex items-center gap-4">
                              <Avatar className="size-12 bg-card ring-1 ring-border ring-offset-2 ring-offset-background">
                                <AvatarFallback className="bg-muted text-base font-semibold text-foreground">
                                  {row.initials}
                                </AvatarFallback>
                              </Avatar>
                              <div className="space-y-1">
                                <TruncatedValue className="max-w-[18rem] text-[17px] font-medium leading-none text-foreground">
                                  {row.name}
                                </TruncatedValue>
                              </div>
                            </div>
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
                              <MailIcon className="-translate-y-px size-4 shrink-0" />
                              <TruncatedValue className="max-w-[14rem] leading-none">{row.email}</TruncatedValue>
                            </div>
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <span className="inline-flex rounded-full bg-muted px-2.5 py-1 text-sm font-medium text-foreground">
                              {row.role}
                            </span>
                          </td>
                          <td className="border-t border-border px-6 py-5">
                            <span
                              className={
                                row.status === 'Actif'
                                  ? 'inline-flex whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                                  : row.status === 'En attente'
                                    ? 'inline-flex whitespace-nowrap rounded-full bg-orange-100 px-2.5 py-1 text-sm font-medium text-orange-700 dark:bg-orange-500/15 dark:text-orange-300'
                                    : 'inline-flex whitespace-nowrap rounded-full bg-red-100 px-2.5 py-1 text-sm font-medium text-red-700 dark:bg-red-500/15 dark:text-red-300'
                              }
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className="border-t border-border px-6 py-5">
                            <span
                              className={
                                row.verification === 'Vérifié'
                                  ? 'inline-flex whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                                  : 'inline-flex whitespace-nowrap rounded-full bg-orange-100 px-2.5 py-1 text-sm font-medium text-orange-700 dark:bg-orange-500/15 dark:text-orange-300'
                              }
                            >
                              {row.verification}
                            </span>
                          </td>
                          <td className="border-t border-border px-6 py-5 align-middle">
                            <div className="inline-flex min-w-0 items-center gap-2 text-[15px] text-muted-foreground">
                              <CalendarIcon className="-translate-y-px size-4 shrink-0" />
                              <TruncatedValue className="max-w-[8rem] leading-none">{row.createdAt}</TruncatedValue>
                            </div>
                          </td>
                          <td className="border-t border-border px-6 py-5 text-right">
                            <Button variant="outline" className="h-10 border-border bg-background px-4 text-[15px] font-medium text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80">
                              Actions
                              <ChevronDownIcon className="size-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>

            <div className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
              <p className="text-sm text-muted-foreground">1-3 sur 3 membres affichés</p>
              <Pagination className="justify-center">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious href="#ui-table" />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink href="#ui-table" isActive>1</PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext href="#ui-table" />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
              <div />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}
